import { Input, Select } from '@/components/ui/FormControls';
import SalesDatePresets from '@/components/sales/SalesDatePresets';
import SalesSelectionPanel from '@/components/sales/SalesSelectionPanel';
import { financialSummary } from '@/lib/salesOverview';
import { useAuth } from '@/contexts/AuthContext';
import DateRangeFields from '@/components/common/DateRangeFields';
import CollapsibleSection from '@/components/common/CollapsibleSection';

// ─────────────────────────────────────────────
// KLOTHIQ — Sales List Page (wired)
// ─────────────────────────────────────────────

import { useState, useMemo, useEffect } from 'react';
import Link from '@/components/common/AccessLink';
import { useSales } from '@/hooks/useSales';

import PageHeader from '@/components/common/PageHeader';
import EmptyState from '@/components/common/EmptyState';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';

import {
  getSalePaymentMethods,
  formatPaymentMethodLabel,
} from '@/components/common/PaymentMethodBadge';
import ExportActions from '@/components/common/ExportActions';
import DeleteSaleModal from '@/components/sales/DeleteSaleModal';
import SalesStatusFilterDropdown from '@/components/sales/SalesStatusFilterDropdown';
import {
  OPERATIONAL_STATUSES,
  COMPOUND_PRESETS,
  matchesStatusFilters,
  getOperationalStatusTags,
} from '@/utils/statusUtils';
import { formatStatus } from '@/utils/pdfHelpers';
import {
  Plus,
  ShoppingCart,
  Search,
  Truck,
  PackageCheck,
  RotateCcw,
  Wallet,
  Undo2,
  AlertTriangle,
  BarChart3,
  X,
} from 'lucide-react';

export default function SalesListPage() {
  const { profile, organization, access, can } = useAuth();
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [activePreset, setActivePreset] = useState<string | null>(null);
  const [kpiFilter, setKpiFilter] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const [paymentFilter, setPaymentFilter] = useState('all'),
    [returnFilter, setReturnFilter] = useState('all'),
    [dueOnly, setDueOnly] = useState(false);
  const [page, setPage] = useState(0),
    [showCreator, setShowCreator] = useState(false);
  const [seller, setSeller] = useState(''),
    [creator, setCreator] = useState(''),
    [from, setFrom] = useState<string>(),
    [to, setTo] = useState<string>();
  const detailedFilterCount = [
    Boolean(from || to),
    Boolean(seller),
    Boolean(creator),
    paymentFilter !== 'all',
    returnFilter !== 'all',
    dueOnly,
  ].filter(Boolean).length;
  useEffect(
    () => setPage(0),
    [
      search,
      selectedStatuses,
      kpiFilter,
      seller,
      creator,
      from,
      to,
      paymentFilter,
      returnFilter,
      dueOnly,
    ],
  );
  useEffect(() => {
    setShowCreator(localStorage.getItem(`sales-show-creator:${profile?.id}`) === 'true');
  }, [profile?.id]);
  // Cards count the complete search scope; table-only filters do not narrow the cards.
  const { data: allSales, isLoading, error, refetch } = useSales({ status: 'all', search });
  const [deletingSale, setDeletingSale] = useState<{
    id: string;
    number: string;
    total: number;
  } | null>(null);

  // Status counts for the 10 operational statuses
  const statusCounts = useMemo(() => {
    const rows = allSales ?? [];
    const counts: Record<string, number> = {};
    for (const op of OPERATIONAL_STATUSES) {
      counts[op.id] = rows.filter((s) => getOperationalStatusTags(s).includes(op.id)).length;
    }
    return counts;
  }, [allSales]);

  // Preset counts
  const presetCounts = useMemo(() => {
    const rows = allSales ?? [];
    const counts: Record<string, number> = {};
    for (const preset of COMPOUND_PRESETS) {
      counts[preset.id] = rows.filter((s) => matchesStatusFilters(s, preset.statuses)).length;
    }
    return counts;
  }, [allSales]);

  const sales = useMemo(() => {
    let rows = allSales ?? [];
    if (kpiFilter?.startsWith('pipeline:')) {
      const stage = kpiFilter.slice(9);
      rows = rows.filter((s) =>
        stage === 'returned'
          ? ['returned', 'partially_returned'].includes(s.sale_status)
          : s.sale_status === stage,
      );
    } else if (kpiFilter === 'pending_delivery') {
      rows = rows.filter((s) =>
        ['booked', 'confirmed', 'delivery_pending'].includes(s.sale_status),
      );
    } else if (kpiFilter === 'delivered') {
      rows = rows.filter((s) => ['delivered', 'partially_returned'].includes(s.sale_status));
    } else if (kpiFilter === 'return_requested') {
      rows = rows.filter((s) => s.return_status === 'requested');
    } else if (kpiFilter === 'payment_pending') {
      rows = rows.filter(
        (s) =>
          ['unpaid', 'partially_paid'].includes(s.payment_status) &&
          s.return_status !== 'completed' &&
          !['returned', 'partially_returned'].includes(s.sale_status),
      );
    } else if (kpiFilter === 'refunded') {
      rows = rows.filter((s) => ['partially_refunded', 'refunded'].includes(s.payment_status));
    } else if (selectedStatuses.length > 0) {
      rows = rows.filter((s) => matchesStatusFilters(s, selectedStatuses));
    }
    return rows.filter(
      (s) =>
        (!seller ||
          (s.sold_by
            ? `profile:${s.sold_by}`
            : s.sold_by_staff_id
              ? `staff:${s.sold_by_staff_id}`
              : 'unknown') === seller) &&
        (!creator || s.created_by === creator) &&
        (!from || s.sale_date >= from) &&
        (!to || s.sale_date <= to) &&
        (paymentFilter === 'all' || s.payment_status === paymentFilter) &&
        (returnFilter === 'all' ||
          (returnFilter === 'none' ? !s.return_status : s.return_status === returnFilter)) &&
        (!dueOnly || (financialSummary(s).amount_due ?? 0) > 0),
    );
  }, [
    allSales,
    selectedStatuses,
    kpiFilter,
    seller,
    creator,
    from,
    to,
    paymentFilter,
    returnFilter,
    dueOnly,
  ]);

  const stats = useMemo(() => {
    const rows = allSales ?? [];
    return {
      pendingDelivery: rows.filter((s) =>
        ['booked', 'confirmed', 'delivery_pending'].includes(s.sale_status),
      ).length,
      delivered: rows.filter((s) => ['delivered', 'partially_returned'].includes(s.sale_status))
        .length,
      returnRequested: rows.filter((s) => s.return_status === 'requested').length,
      paymentPending: rows.filter(
        (s) =>
          ['unpaid', 'partially_paid'].includes(s.payment_status) &&
          s.return_status !== 'completed' &&
          !['returned', 'partially_returned'].includes(s.sale_status),
      ).length,
      refunded: rows.filter((s) => ['partially_refunded', 'refunded'].includes(s.payment_status))
        .length,
      totalCount: rows.length,
    };
  }, [allSales]);

  // Visual Sales Pipeline calculation
  const pipeline = useMemo(() => {
    const rows = allSales ?? [];
    const total = rows.length || 1;
    const stages = [
      {
        key: 'draft',
        label: 'Draft',
        count: rows.filter((s) => s.sale_status === 'draft').length,
        color: 'bg-blue-500',
      },
      {
        key: 'booked',
        label: 'Booked',
        count: rows.filter((s) => s.sale_status === 'booked').length,
        color: 'bg-amber-500',
      },
      {
        key: 'confirmed',
        label: 'Confirmed',
        count: rows.filter((s) => s.sale_status === 'confirmed').length,
        color: 'bg-indigo-500',
      },
      {
        key: 'delivery_pending',
        label: 'Delivery Pending',
        count: rows.filter((s) => s.sale_status === 'delivery_pending').length,
        color: 'bg-purple-500',
      },
      {
        key: 'delivered',
        label: 'Delivered',
        count: rows.filter((s) => s.sale_status === 'delivered').length,
        color: 'bg-emerald-500',
      },
      {
        key: 'returned',
        label: 'Returned',
        count: rows.filter((s) => ['partially_returned', 'returned'].includes(s.sale_status))
          .length,
        color: 'bg-slate-400',
      },
    ];
    stages.push({
      key: 'cancelled',
      label: 'Cancelled',
      count: rows.filter((s) => s.sale_status === 'cancelled').length,
      color: 'bg-red-400',
    });
    return stages.map((st) => ({
      ...st,
      pct: (st.count / total) * 100,
    }));
  }, [allSales]);

  // Urgent operational alerts
  const actionAlerts = useMemo(() => {
    const rows = allSales ?? [];
    const pendingReturns = rows.filter((s) => s.return_status === 'requested');
    const now = Date.now();
    const staleDeliveries = rows.filter((s) => {
      if (s.sale_status !== 'delivery_pending') return false;
      const saleTime = new Date(s.sale_date).getTime();
      return now - saleTime > 48 * 3600 * 1000;
    });
    return {
      pendingReturnsCount: pendingReturns.length,
      staleDeliveriesCount: staleDeliveries.length,
    };
  }, [allSales]);

  const handleKpiCardClick = (filterKey: string) => {
    if (kpiFilter === filterKey) {
      setKpiFilter(null);
    } else {
      setKpiFilter(filterKey);
      setSelectedStatuses([]);
      setActivePreset(null);
    }
  };

  const handleToggleStatus = (statusId: string) => {
    setKpiFilter(null);
    setActivePreset(null);
    setSelectedStatuses((prev) => {
      if (prev.includes(statusId)) {
        return prev.filter((id) => id !== statusId);
      } else {
        return [...prev, statusId];
      }
    });
  };

  const handleSelectPreset = (preset: (typeof COMPOUND_PRESETS)[0]) => {
    setKpiFilter(null);
    if (activePreset === preset.id) {
      setActivePreset(null);
      setSelectedStatuses([]);
    } else {
      setActivePreset(preset.id);
      setSelectedStatuses([...preset.statuses]);
    }
  };

  const handlePipelineClick = (stageKey: string) => {
    setKpiFilter(`pipeline:${stageKey}`);
    setActivePreset(null);
    setSelectedStatuses([]);
  };

  const clearAllFilters = () => {
    setSelectedStatuses([]);
    setActivePreset(null);
    setKpiFilter(null);
    setSearch('');
    setSeller('');
    setCreator('');
    setFrom(undefined);
    setTo(undefined);
    setPaymentFilter('all');
    setReturnFilter('all');
    setDueOnly(false);
  };

  const activeStatusLabels = selectedStatuses
    .map((id) => OPERATIONAL_STATUSES.find((s) => s.id === id)?.label)
    .filter(Boolean);

  const filterSummaryText =
    [
      kpiFilter ? `Filter: ${kpiFilter.replace(/_/g, ' ')}` : null,
      activePreset ? `Preset: ${COMPOUND_PRESETS.find((p) => p.id === activePreset)?.label}` : null,
      activeStatusLabels.length > 0 ? `Statuses: ${activeStatusLabels.join(' + ')}` : null,
      seller ? `Seller ID: ${seller}` : null,
      creator ? `Entered by ID: ${creator}` : null,
      from ? `From: ${from}` : null,
      to ? `To: ${to}` : null,
      paymentFilter !== 'all' ? `Payment: ${paymentFilter}` : null,
      returnFilter !== 'all' ? `Return: ${returnFilter}` : null,
      dueOnly ? 'Known due only' : null,
      search ? `Search: "${search}"` : null,
    ]
      .filter(Boolean)
      .join(' | ') || 'All Sales';

  const exportSalesData = useMemo(
    () =>
      (sales ?? []).map((s) => {
        const raw = s as unknown as Record<string, unknown>;
        return {
          ...s,
          sale_number: s.sale_number,
          sale_date: s.sale_date,
          customer_name: s.customer_name || s.customers?.name || 'Walk-in Customer',
          seller_name: (raw.seller_name as string) ?? '—',
          item_count: s.item_count ?? 0,
          subtotal: Number(raw.subtotal_amount ?? s.total_amount ?? 0),
          discount: Number(raw.discount_amount ?? 0),
          total: Number(s.total_amount ?? 0),
          paid_amount: financialSummary(s).net_collected,
          balance_due: financialSummary(s).amount_due,
          return_review: financialSummary(s).return_review,
          entered_by_name: s.entered_by_name,
          status: s.sale_status,
          payment_status: s.payment_status,
          payment_method: getSalePaymentMethods(s).map(formatPaymentMethodLabel).join(', ') || '—',
        };
      }),
    [sales],
  );

  const hasFilters = Boolean(
    search ||
      selectedStatuses.length ||
      kpiFilter ||
      seller ||
      creator ||
      from ||
      to ||
      paymentFilter !== 'all' ||
      returnFilter !== 'all' ||
      dueOnly,
  );
  return (
    <div className="space-y-4">
      <PageHeader
        title="Sales"
        description="Intelligent tracking & order lifecycle management"
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {can('financials.view') && (
              <span className="text-xs text-muted-foreground">Export all matching:</span>
            )}
            <ExportActions
              disabled={isLoading || !!error || (!!from && !!to && from > to)}
              exportType="sales"
              data={exportSalesData as unknown as Record<string, unknown>[]}
              excelOptions={() => {
                const rows = sales ?? [];
                const totalRev = rows.reduce((acc, s) => acc + (Number(s.total_amount) || 0), 0);
                const totalPaid = rows.reduce(
                  (acc, s) => acc + financialSummary(s).net_collected,
                  0,
                );
                const totalPending = rows.reduce((acc, s) => {
                  return acc + (financialSummary(s).amount_due ?? 0);
                }, 0);
                const pendingOrders = rows.filter(
                  (s) => (financialSummary(s).amount_due ?? 0) > 0,
                ).length;
                const returnedOrders = rows.filter(
                  (s) =>
                    ['returned', 'partially_returned'].includes(s.sale_status) ||
                    s.return_status === 'completed',
                ).length;

                return {
                  fileName: `Sales_Report_${new Date().toISOString().split('T')[0]}`,
                  sheetName: 'Sales',
                  filtersSummary: filterSummaryText,
                  includeTotalsRow: true,
                  columns: [
                    { header: 'Sale #', key: 'sale_number' },
                    { header: 'Customer', key: 'customer' },
                    { header: 'Phone', key: 'phone' },
                    { header: 'Seller', key: 'seller_name' },
                    { header: 'Entered by', key: 'entered_by_name' },
                    { header: 'Net collected', key: 'net_collected', format: 'currency' },
                    { header: 'Date', key: 'sale_date', format: 'date' },
                    {
                      header: 'Total Amount (₹)',
                      key: 'total_amount',
                      format: 'currency',
                      width: 18,
                    },
                    { header: 'Known due / review', key: 'balance_due', width: 20 },
                    { header: 'Status', key: 'sale_status' },
                    { header: 'Payment Status', key: 'payment_status' },
                    { header: 'Payment Method', key: 'payment_method' },
                  ],
                  data: rows.map((s) => {
                    const isUnpaid = s.payment_status?.toLowerCase() === 'unpaid';
                    return {
                      sale_number: s.sale_number,
                      customer: s.customer_name || s.customers?.name || 'Walk-in Customer',
                      phone: s.customer_mobile || s.customers?.phone || '—',
                      sale_date: s.sale_date,
                      total_amount: Number(s.total_amount),
                      balance_due: financialSummary(s).return_review
                        ? 'Review return'
                        : (financialSummary(s).amount_due ?? 'Not applicable'),
                      seller_name: s.seller_name,
                      entered_by_name: s.entered_by_name,
                      net_collected: financialSummary(s).net_collected,
                      sale_status: formatStatus(s.sale_status),
                      payment_status: isUnpaid ? 'UNPAID' : formatStatus(s.payment_status),
                      payment_method:
                        getSalePaymentMethods(s).map(formatPaymentMethodLabel).join(', ') || '—',
                    };
                  }),
                  summaryMetrics: [
                    { label: 'Total Orders', value: rows.length, format: 'integer' },
                    { label: 'Original invoice total', value: totalRev, format: 'currency' },
                    { label: 'Net collected', value: totalPaid, format: 'currency' },
                    {
                      label: 'Known due (returns excluded)',
                      value: totalPending,
                      format: 'currency',
                    },
                    { label: 'Orders Awaiting Payment', value: pendingOrders, format: 'integer' },
                    { label: 'Returned Orders', value: returnedOrders, format: 'integer' },
                  ],
                };
              }}
            />
            <Link
              to="/sales/new"
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" /> New Sale
            </Link>
          </div>
        }
      />

      {/* Action Alerts Banner (Actionable items requiring attention) */}
      {actionAlerts.pendingReturnsCount > 0 && (
        <div className="flex items-center justify-between rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
          <div className="flex items-center gap-2.5">
            <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0" />
            <span>
              <strong>
                {actionAlerts.pendingReturnsCount} return request
                {actionAlerts.pendingReturnsCount !== 1 ? 's' : ''}
              </strong>{' '}
              currently awaiting review and manager decision.
            </span>
          </div>
          <button
            type="button"
            onClick={() => handleKpiCardClick('return_requested')}
            className="rounded bg-amber-600 px-3 py-1 text-xs font-semibold text-white shadow-sm hover:bg-amber-700 transition-colors shrink-0"
          >
            Review Returns
          </button>
        </div>
      )}

      <CollapsibleSection
        title="Sales overview"
        description="Delivery, payments and fulfillment pipeline"
      >
        <p className="text-xs text-muted-foreground">
          Operational cards and pipeline:{' '}
          {search ? 'all orders matching the search' : 'all organization orders'}. Date and person
          filters below affect the order list.
        </p>
        {/* Interactive Smart KPI Dashboard (Click to Filter) */}
        {!isLoading && !error && (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {[
                {
                  key: 'pending_delivery',
                  label: 'Pending Delivery',
                  value: stats.pendingDelivery,
                  icon: Truck,
                  color: 'amber',
                },
                {
                  key: 'delivered',
                  label: 'Delivered',
                  value: stats.delivered,
                  icon: PackageCheck,
                  color: 'green',
                },
                {
                  key: 'return_requested',
                  label: 'Return Requested',
                  value: stats.returnRequested,
                  icon: RotateCcw,
                  color: 'amber',
                },
                {
                  key: 'payment_pending',
                  label: 'Payment Pending',
                  value: stats.paymentPending,
                  icon: Wallet,
                  color: 'red',
                },
                {
                  key: 'refunded',
                  label: 'Refund Recorded',
                  value: stats.refunded,
                  icon: Undo2,
                  color: 'grey',
                },
              ].map(({ key, label, value, icon: Icon, color }) => {
                const isSelected = kpiFilter === key;
                const colorClasses: Record<string, string> = {
                  amber: 'text-amber-600 dark:text-amber-400',
                  green: 'text-emerald-600 dark:text-emerald-400',
                  red: 'text-destructive',
                  grey: 'text-muted-foreground',
                };
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleKpiCardClick(key)}
                    className={`obsidian-surface obsidian-interactive rounded-lg border p-3 text-left transition-all ${
                      isSelected
                        ? 'border-primary bg-primary/5 ring-2 ring-primary shadow-sm'
                        : 'border-border bg-card hover:border-primary/40 hover:bg-muted/20'
                    }`}
                    title={`Click to filter by ${label}`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        {label}
                      </p>
                      <Icon className={`h-3.5 w-3.5 ${colorClasses[color]}`} />
                    </div>
                    <div className="mt-1 flex items-baseline justify-between">
                      <p className={`text-lg font-bold ${colorClasses[color]}`}>{value}</p>
                      {isSelected && (
                        <span className="text-[10px] font-semibold text-primary uppercase">
                          Filtered
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Visual Fulfillment Pipeline Funnel Bar */}
            <div className="obsidian-surface rounded-lg border border-border bg-card p-3 shadow-sm">
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground">
                  <BarChart3 className="h-3.5 w-3.5 text-primary" />
                  <span>Fulfillment Pipeline Distribution ({stats.totalCount} Orders)</span>
                </div>
                <span className="text-[11px] text-muted-foreground">Click a stage to filter</span>
              </div>
              {/* Segmented bar */}
              <div className="flex h-3.5 w-full overflow-hidden rounded-full bg-muted/40">
                {pipeline.map((stage) => {
                  if (stage.count === 0) return null;
                  return (
                    <button
                      key={stage.key}
                      type="button"
                      onClick={() => handlePipelineClick(stage.key)}
                      style={{ width: `${stage.pct}%` }}
                      className={`${stage.color} hover:brightness-110 transition-all border-r border-background/20 last:border-0`}
                      title={`${stage.label}: ${stage.count} (${stage.pct.toFixed(1)}%)`}
                    />
                  );
                })}
              </div>
              {/* Pipeline legend */}
              <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-muted-foreground">
                {pipeline.map((stage) => {
                  const isStageActive =
                    kpiFilter === `pipeline:${stage.key}` ||
                    selectedStatuses.includes(stage.key) ||
                    (stage.key === 'returned' && selectedStatuses.includes('return_completed'));
                  return (
                    <button
                      key={stage.key}
                      type="button"
                      onClick={() => handlePipelineClick(stage.key)}
                      className={`flex items-center gap-1.5 hover:text-foreground transition-colors ${
                        isStageActive ? 'font-bold text-primary' : ''
                      }`}
                    >
                      <span className={`h-2 w-2 rounded-full ${stage.color}`} />
                      <span>
                        {stage.label}: {stage.count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </CollapsibleSection>
      {/* Search, Status Dropdown Filter & Active Filter Chips Bar */}
      <div className="space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-2.5">
          {/* Left: Search input + Status Filter Dropdown */}
          <div className="flex flex-1 flex-wrap items-center gap-2 min-w-0">
            <div className="relative flex-1 sm:flex-initial sm:w-64">
              <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              <Input
                id="sales-search"
                type="text"
                placeholder="Search sale #, customer, phone…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background pl-8 pr-3 text-xs placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                helpLabel="Search records"
              />
            </div>

            {/* Replaced wall of buttons with elegant Multi-Select Filter Dropdown */}
            <SalesStatusFilterDropdown
              selectedStatuses={selectedStatuses}
              onToggleStatus={handleToggleStatus}
              activePreset={activePreset}
              onSelectPreset={handleSelectPreset}
              onClearFilters={clearAllFilters}
              statusCounts={statusCounts}
              presetCounts={presetCounts}
              totalCount={stats.totalCount}
            />

            {/* Quick reset button */}
            {(kpiFilter || selectedStatuses.length > 0 || activePreset || search) && (
              <button
                type="button"
                onClick={clearAllFilters}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
                title="Reset all search and status filters"
              >
                <X className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Reset Filter</span>
              </button>
            )}
          </div>

          {/* Right: Results count */}
          <div className="text-xs text-muted-foreground font-medium shrink-0">
            Showing <span className="font-bold text-foreground">{sales.length}</span> of{' '}
            {stats.totalCount} orders
          </div>
        </div>

        {/* Active Filter Chips Strip: Appears ONLY when filters are active, ultra-clean on mobile */}
        {(selectedStatuses.length > 0 || activePreset || kpiFilter) && (
          <div className="flex flex-wrap items-center gap-1.5 pt-1 text-xs">
            <span className="text-[11px] font-semibold text-muted-foreground mr-1">Active:</span>

            {/* KPI Card Filter Tag */}
            {kpiFilter && (
              <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                KPI: {kpiFilter.replace(/_/g, ' ')}
                <button
                  type="button"
                  onClick={() => setKpiFilter(null)}
                  className="rounded-full hover:bg-primary/20 p-0.5"
                  aria-label="Remove KPI filter"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            {/* Preset Tag */}
            {activePreset && (
              <span className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                Preset: {COMPOUND_PRESETS.find((p) => p.id === activePreset)?.label}
                <button
                  type="button"
                  onClick={() => {
                    setActivePreset(null);
                    setSelectedStatuses([]);
                  }}
                  className="rounded-full hover:bg-primary/20 p-0.5"
                  aria-label="Remove preset filter"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            )}

            {/* Individual Status Tags */}
            {selectedStatuses.map((statusId) => {
              const op = OPERATIONAL_STATUSES.find((s) => s.id === statusId);
              if (!op) return null;
              return (
                <span
                  key={statusId}
                  className="inline-flex items-center gap-1 rounded-full border border-border bg-card px-2.5 py-0.5 text-xs font-medium text-foreground shadow-2xs"
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${op.color === 'amber' ? 'bg-amber-500' : op.color === 'blue' ? 'bg-blue-500' : op.color === 'green' ? 'bg-emerald-500' : op.color === 'red' ? 'bg-destructive' : 'bg-muted-foreground'}`}
                  />
                  <span>{op.label}</span>
                  <button
                    type="button"
                    onClick={() => handleToggleStatus(statusId)}
                    className="rounded-full hover:bg-muted p-0.5 text-muted-foreground hover:text-foreground"
                    aria-label={`Remove ${op.label} filter`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              );
            })}

            <button
              type="button"
              onClick={clearAllFilters}
              className="text-[11px] text-muted-foreground hover:text-foreground hover:underline ml-1"
            >
              Clear all
            </button>
          </div>
        )}
      </div>

      <CollapsibleSection
        title="More filters"
        description="Dates, people, payment and returns"
        badge={
          detailedFilterCount > 0 ? (
            <span className="shrink-0 rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
              {detailedFilterCount} active
            </span>
          ) : undefined
        }
      >
        <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <DateRangeFields from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
          <label className="block min-w-0 text-xs text-muted-foreground">
            Seller
            <Select
              value={seller}
              onChange={(e) => setSeller(e.target.value)}
              className="mt-1 min-h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground"
              helpLabel="Seller"
            >
              <option value="">All sellers</option>
              {Array.from(
                new Map(
                  (allSales ?? []).map((s) => [
                    s.sold_by
                      ? `profile:${s.sold_by}`
                      : s.sold_by_staff_id
                        ? `staff:${s.sold_by_staff_id}`
                        : 'unknown',
                    s.seller_name || 'Seller not recorded',
                  ]),
                ).entries(),
              ).map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </Select>
          </label>
          <label className="block min-w-0 text-xs text-muted-foreground">
            Entered by
            <Select
              value={creator}
              onChange={(e) => setCreator(e.target.value)}
              className="mt-1 min-h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground"
              helpLabel="Entered by"
            >
              <option value="">All creators</option>
              {Array.from(
                new Map(
                  (allSales ?? []).map((s) => [
                    s.created_by,
                    s.entered_by_name || 'Creator not recorded',
                  ]),
                ).entries(),
              ).map(([id, name]) => (
                <option key={id} value={id ?? ''}>
                  {name}
                </option>
              ))}
            </Select>
          </label>
          <label className="flex min-h-11 items-center gap-2 text-sm">
            <Input
              type="checkbox"
              checked={showCreator}
              onChange={(e) => {
                setShowCreator(e.target.checked);
                localStorage.setItem(`sales-show-creator:${profile?.id}`, String(e.target.checked));
              }}
              helpLabel="Show entered by"
            />
            Show entered by
          </label>
        </div>
        <p className="text-xs text-muted-foreground">
          Invoice amounts are before return adjustments. Known due excludes drafts, cancelled orders
          and returns awaiting balance review. Export includes all matching orders.
        </p>
        <div className="grid items-end gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <label className="block min-w-0 text-xs text-muted-foreground">
            Payment status
            <Select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className="mt-1 min-h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground"
              helpLabel="Payment status"
            >
              {['all', 'unpaid', 'partially_paid', 'paid', 'partially_refunded', 'refunded'].map(
                (v) => (
                  <option key={v} value={v}>
                    {v.replaceAll('_', ' ')}
                  </option>
                ),
              )}
            </Select>
          </label>
          <label className="block min-w-0 text-xs text-muted-foreground">
            Return status
            <Select
              value={returnFilter}
              onChange={(e) => setReturnFilter(e.target.value)}
              className="mt-1 min-h-11 w-full rounded-lg border border-border bg-background px-3 text-sm text-foreground"
              helpLabel="Return status"
            >
              {['all', 'none', 'requested', 'accepted', 'rejected', 'completed'].map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </Select>
          </label>
          <label className="flex min-h-11 items-center gap-2 text-sm">
            <Input
              type="checkbox"
              checked={dueOnly}
              onChange={(e) => setDueOnly(e.target.checked)}
              helpLabel="Known due only"
            />
            Known due only
          </label>
          <div className="flex min-h-11 flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={clearAllFilters}
              className="min-h-11 px-3 text-sm text-primary"
            >
              Reset filters
            </button>
            {showCreator && (
              <button
                type="button"
                className="min-h-11 px-3 text-sm text-muted-foreground"
                onClick={() => {
                  setShowCreator(false);
                  localStorage.setItem(`sales-show-creator:${profile?.id}`, 'false');
                }}
              >
                Reset columns
              </button>
            )}
          </div>
        </div>
        <SalesDatePresets
          onChange={(r) => {
            setFrom(r.from);
            setTo(r.to);
          }}
          onClear={() => {
            setFrom(undefined);
            setTo(undefined);
          }}
        />
      </CollapsibleSection>
      {from && to && from > to && (
        <p role="alert" className="text-sm text-destructive">
          Choose an end date on or after the start date.
        </p>
      )}
      {isLoading && <LoadingState message="Loading sales…" />}
      {error && <ErrorState message={error.message} onRetry={refetch} />}

      {!isLoading && !error && sales?.length === 0 && (
        <EmptyState
          icon={<ShoppingCart className="h-12 w-12" />}
          title="No sales found"
          description={hasFilters ? 'Try clearing filters' : 'Create your first sale order'}
          action={
            !hasFilters ? (
              <Link
                to="/sales/new"
                className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
              >
                <Plus className="h-4 w-4" /> New Sale
              </Link>
            ) : undefined
          }
        />
      )}

      {!isLoading && !error && sales && sales.length > 0 && (
        <>
          <SalesSelectionPanel
            key={JSON.stringify([
              organization?.id,
              profile?.id,
              access?.access_version,
              can('sales.view'),
              search,
              selectedStatuses,
              activePreset,
              kpiFilter,
              seller,
              creator,
              from,
              to,
              paymentFilter,
              returnFilter,
              dueOnly,
            ])}
            rows={sales}
            page={page}
            organizationId={organization?.id || ''}
            disabled={!organization?.id || !can('sales.view') || (!!from && !!to && from > to)}
            showCreator={showCreator}
            onDelete={(s) =>
              setDeletingSale({ id: s.id, number: s.sale_number, total: Number(s.total_amount) })
            }
            footer={
              <div className="mt-3 flex flex-wrap items-center justify-end gap-3 text-sm">
                <button
                  type="button"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                  className="min-h-11 rounded-lg border border-border px-3 disabled:opacity-40"
                >
                  Previous
                </button>
                <span>
                  {sales.length} orders · Page {page + 1} of{' '}
                  {Math.max(1, Math.ceil(sales.length / 50))}
                </span>
                <button
                  type="button"
                  disabled={(page + 1) * 50 >= sales.length}
                  onClick={() => setPage((p) => p + 1)}
                  className="min-h-11 rounded-lg border border-border px-3 disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            }
          />
        </>
      )}

      {/* Delete Sale Modal */}
      {deletingSale && (
        <DeleteSaleModal
          isOpen={!!deletingSale}
          onClose={() => setDeletingSale(null)}
          saleId={deletingSale.id}
          saleNumber={deletingSale.number}
          totalAmount={deletingSale.total}
          onSuccess={() => refetch()}
        />
      )}
    </div>
  );
}
